import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import { Pagination } from "@/components/Pagination";
import { Plus, Search, Package, Star, Share2, LayoutGrid, List, Play, Upload, X, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { ShareCatalogDialog } from "@/components/ShareCatalogDialog";
import { toast } from "sonner";

const PAGE_SIZE = 24;

const WEIGHT_BUCKETS = [
  { key: "all", label: "Any weight", min: null, max: null },
  { key: "0-5", label: "0 – 5 g", min: 0, max: 5 },
  { key: "5-15", label: "5 – 15 g", min: 5, max: 15 },
  { key: "15-30", label: "15 – 30 g", min: 15, max: 30 },
  { key: "30-60", label: "30 – 60 g", min: 30, max: 60 },
  { key: "60+", label: "60 g +", min: 60, max: null },
];

export default function ProductsListPage() {
  const nav = useNavigate();
  const { user, company } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ search: "", metal: "all", status: "all", category_id: "all", sub_category: "all", label: "all", weight_bucket: "all" });
  const [view, setView] = useState("grid"); // grid | list
  const [selected, setSelected] = useState([]); // for presentation mode

  const canEdit = can(user, "catalog.edit");
  const canPresent = can(user, "catalog.presentation");
  const canBulk = can(user, "catalog.bulk_import");
  const canShare = can(user, "catalog.share");
  const canSeePrice = can(user, "orders.see_pricing");
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    const params = { page, page_size: PAGE_SIZE };
    if (filters.search) params.search = filters.search;
    if (filters.metal !== "all") params.metal = filters.metal;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.category_id !== "all") params.category_id = filters.category_id;
    if (filters.sub_category !== "all") params.sub_category = filters.sub_category;
    if (filters.label !== "all") params.label = filters.label;
    const b = WEIGHT_BUCKETS.find(x => x.key === filters.weight_bucket);
    if (b?.min != null) params.weight_min = b.min;
    if (b?.max != null) params.weight_max = b.max;
    const { data } = await api.get("/products", { params });
    setItems(data.items || []);
    setTotal(data.total || 0);
  };
  useEffect(() => { load(); }, [filters, page]);
  useEffect(() => { setPage(1); }, [filters]);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/product-categories"); setCategories(data || []); } catch {}
  })(); }, []);

  // Derive sub-category & label options from current page (best-effort)
  const subCategories = useMemo(() => Array.from(new Set(items.map(p => p.sub_category).filter(Boolean))), [items]);
  const labelOptions = useMemo(() => Array.from(new Set(items.flatMap(p => p.labels || []))), [items]);

  const storefrontUrl = company ? `${window.location.origin}/store/${company.code}` : "";
  const copyStorefront = () => { navigator.clipboard.writeText(storefrontUrl); toast.success("Storefront link copied"); };

  const toggleSelect = (id) => setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  const startPresentation = () => {
    const ids = selected.length ? selected : items.map(p => p.id);
    if (!ids.length) return toast.error("No products to present");
    window.open(`/products/present?ids=${ids.join(",")}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="products-list-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Catalog</div>
          <h1 className="font-serif text-3xl lg:text-4xl font-medium">Products</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={copyStorefront} className="rounded-full" data-testid="copy-storefront-btn">
            <Share2 size={14} className="mr-1"/> Storefront Link
          </Button>
          {canShare && (
            <Button
              onClick={() => {
                if (!selected.length) return toast.error("Pick products to share (checkbox on each card)");
                setShareOpen(true);
              }}
              className="rounded-full bg-[#25D366] hover:bg-[#1FAE55] text-white"
              data-testid="share-whatsapp-btn"
            >
              <MessageCircle size={14} className="mr-1"/> WhatsApp ({selected.length})
            </Button>
          )}
          {canPresent && (
            <Button variant="outline" onClick={startPresentation} className="rounded-full border-[#C9A24B] text-[#6E5416] hover:bg-[#F6EED8]" data-testid="presentation-btn">
              <Play size={14} className="mr-1"/> Presentation ({selected.length || items.length})
            </Button>
          )}
          {canBulk && (
            <Button variant="outline" onClick={() => nav("/products/bulk-add")} className="rounded-full" data-testid="bulk-add-btn">
              <Upload size={14} className="mr-1"/> Bulk Add
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => nav("/products/new")} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="new-product-btn">
              <Plus size={14} className="mr-1"/> New Product
            </Button>
          )}
        </div>
      </div>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="relative md:col-span-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5A50]"/>
              <Input placeholder="Search title, design code, SKU…" className="pl-9 rounded-lg" value={filters.search} onChange={(e)=>setFilters({...filters, search:e.target.value})} data-testid="products-search"/>
            </div>
            <Select value={filters.category_id} onValueChange={(v)=>setFilters({...filters, category_id:v})}>
              <SelectTrigger className="rounded-lg" data-testid="filter-category"><SelectValue placeholder="Category"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.metal} onValueChange={(v)=>setFilters({...filters, metal:v})}>
              <SelectTrigger className="rounded-lg" data-testid="filter-metal"><SelectValue placeholder="Metal"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All metals</SelectItem>
                {["gold","silver","platinum","diamond","other"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.weight_bucket} onValueChange={(v)=>setFilters({...filters, weight_bucket:v})}>
              <SelectTrigger className="rounded-lg" data-testid="filter-weight"><SelectValue placeholder="Weight"/></SelectTrigger>
              <SelectContent>{WEIGHT_BUCKETS.map(b => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v)=>setFilters({...filters, status:v})}>
              <SelectTrigger className="rounded-lg" data-testid="filter-product-status"><SelectValue placeholder="Status"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {["in-stock","made-to-order","sold","hidden"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(subCategories.length > 0 || labelOptions.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {subCategories.length > 0 && (
                <Select value={filters.sub_category} onValueChange={(v)=>setFilters({...filters, sub_category:v})}>
                  <SelectTrigger className="rounded-lg" data-testid="filter-subcategory"><SelectValue placeholder="Sub-category"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sub-categories</SelectItem>
                    {subCategories.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {labelOptions.length > 0 && (
                <Select value={filters.label} onValueChange={(v)=>setFilters({...filters, label:v})}>
                  <SelectTrigger className="rounded-lg" data-testid="filter-label"><SelectValue placeholder="Label"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All labels</SelectItem>
                    {labelOptions.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="md:col-start-4 flex items-center justify-end gap-2">
                <div className="text-xs text-[#4A5A50]">View</div>
                <button onClick={() => setView("grid")} className={`p-2 rounded-lg ${view==="grid" ? "bg-[#0F5E39] text-white" : "bg-[#F1EDDF] text-[#4A5A50]"}`} data-testid="view-grid"><LayoutGrid size={14}/></button>
                <button onClick={() => setView("list")} className={`p-2 rounded-lg ${view==="list" ? "bg-[#0F5E39] text-white" : "bg-[#F1EDDF] text-[#4A5A50]"}`} data-testid="view-list"><List size={14}/></button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {selected.length > 0 && (
        <div className="flex items-center justify-between bg-[#F6EED8] border border-[#C9A24B]/30 rounded-lg px-4 py-2 text-sm">
          <span className="text-[#6E5416]"><strong>{selected.length}</strong> selected for presentation</span>
          <button onClick={() => setSelected([])} className="text-[#6E5416] hover:text-[#0F5E39]" data-testid="clear-selection"><X size={14}/></button>
        </div>
      )}

      {items.length === 0 ? (
        <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="py-16 text-center">
          <Package size={32} className="text-[#C9A24B] mx-auto mb-3" strokeWidth={1.5}/>
          <div className="font-serif text-xl">No products match your filters</div>
          <div className="text-[#4A5A50] text-sm mt-1">Try adjusting filters or add your first product.</div>
        </CardContent></Card>
      ) : view === "grid" ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map(p => (
              <div key={p.id} className="relative">
                {canPresent && (
                  <label className="absolute top-2 right-2 z-10 bg-white/90 rounded p-1 cursor-pointer" onClick={(e)=>e.stopPropagation()}>
                    <input type="checkbox" checked={selected.includes(p.id)} onChange={()=>toggleSelect(p.id)} data-testid={`select-${p.design_code || p.id}`}/>
                  </label>
                )}
                <button onClick={() => nav(`/products/${p.id}`)} className="block w-full text-left bg-white rounded-xl border border-[#E4DFCF] overflow-hidden card-hover" data-testid={`product-card-${p.design_code || p.sku || p.id}`}>
                  <div className="aspect-square bg-[#F1EDDF] relative overflow-hidden">
                    {p.media?.[0] ? (
                      <img src={fileUrl(p.media[0].thumb_url || p.media[0].url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#C9A24B]"><Package size={32} strokeWidth={1.2}/></div>
                    )}
                    {p.featured && (
                      <div className="absolute top-2 left-2 bg-[#C9A24B] text-white text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Star size={10}/> Featured
                      </div>
                    )}
                    {p.labels?.length > 0 && (
                      <div className="absolute bottom-2 left-2 flex flex-wrap gap-1">
                        {p.labels.slice(0, 2).map(l => <span key={l} className="text-[9px] uppercase tracking-widest bg-black/60 text-white px-1.5 py-0.5 rounded">{l}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#0F5E39] font-semibold">{p.design_code || p.sku || "—"}</div>
                    <div className="font-serif text-lg mt-0.5 line-clamp-1">{p.title}</div>
                    <div className="text-xs text-[#4A5A50] line-clamp-1">{p.sub_category || p.product_category_name} · {p.metal} {p.purity || ""}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="font-serif text-lg font-medium text-[#6E5416]">{p.weight_grams ?? p.net_weight ?? "—"} g</div>
                      {canSeePrice && p.is_price_visible ? (
                        <div className="text-sm font-medium text-[#C9A24B]">₹{(p.computed_price || 0).toLocaleString('en-IN')}</div>
                      ) : null}
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage}/>
        </>
      ) : (
        <>
          <Card className="border-[#E4DFCF] rounded-xl">
            <CardContent className="p-0 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#F1EDDF] text-[10px] uppercase tracking-widest text-[#4A5A50]">
                  <tr>
                    {canPresent && <th className="px-4 py-3"></th>}
                    <th className="px-4 py-3 text-left">Image</th>
                    <th className="px-4 py-3 text-left">Design Code</th>
                    <th className="px-4 py-3 text-left">Title</th>
                    <th className="px-4 py-3 text-right font-semibold text-[#0F5E39]">Weight (g)</th>
                    <th className="px-4 py-3 text-left">Metal</th>
                    <th className="px-4 py-3 text-left">Sub-cat</th>
                    <th className="px-4 py-3 text-left">Labels</th>
                    {canSeePrice && <th className="px-4 py-3 text-right">Price</th>}
                    <th className="px-4 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E4DFCF]">
                  {items.map(p => (
                    <tr key={p.id} className="hover:bg-[#FAF7EF] cursor-pointer" onClick={() => nav(`/products/${p.id}`)} data-testid={`product-row-${p.design_code || p.id}`}>
                      {canPresent && (
                        <td className="px-4 py-2" onClick={(e)=>e.stopPropagation()}>
                          <input type="checkbox" checked={selected.includes(p.id)} onChange={()=>toggleSelect(p.id)}/>
                        </td>
                      )}
                      <td className="px-4 py-2">
                        <div className="w-12 h-12 rounded bg-[#F1EDDF] overflow-hidden">
                          {p.media?.[0] ? <img src={fileUrl(p.media[0].thumb_url || p.media[0].url)} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center"><Package size={16} className="text-[#C9A24B]"/></div>}
                        </div>
                      </td>
                      <td className="px-4 py-2 font-medium text-[#0F5E39]">{p.design_code || p.sku || "—"}</td>
                      <td className="px-4 py-2">{p.title}</td>
                      <td className="px-4 py-2 text-right font-serif text-lg font-semibold text-[#6E5416]">{p.weight_grams ?? p.net_weight ?? "—"}</td>
                      <td className="px-4 py-2">{p.metal} {p.purity}</td>
                      <td className="px-4 py-2">{p.sub_category || p.product_category_name || "—"}</td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(p.labels || []).slice(0, 3).map(l => <span key={l} className="text-[10px] bg-[#F6EED8] text-[#6E5416] px-1.5 py-0.5 rounded-full">{l}</span>)}
                        </div>
                      </td>
                      {canSeePrice && <td className="px-4 py-2 text-right">{p.is_price_visible ? `₹${(p.computed_price || 0).toLocaleString('en-IN')}` : "—"}</td>}
                      <td className="px-4 py-2"><StatusPill status={p.status === "in-stock" ? "accepted" : p.status === "made-to-order" ? "in-progress" : p.status === "sold" ? "completed" : "on-hold"} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage}/>
        </>
      )}

      {canShare && (
        <ShareCatalogDialog
          open={shareOpen}
          setOpen={setShareOpen}
          products={items.filter(p => selected.includes(p.id))}
          onCreated={() => setSelected([])}
        />
      )}
    </div>
  );
}
